// Logos, icons, and image constants
// Brand logo — Brand Guidelines V3
import SKYSPECS_BRAND_LOGO from '/logos/skyspecs/skyspecs-primary-blacktext.svg';
import SKYSPECS_BRAND_LOGO_WHITE from '/logos/skyspecs/skyspecs-primary-white.svg';
import SKYSPECS_BRAND_ICON_WHITE from '/logos/skyspecs/skyspecs-icon-white.svg';
import TURBINE_ICON from '/icons/wind-turbine.svg';
import FULL_TURBINE_IMAGE from '/icons/turbine_outline.svg';
import FULL_TURBINE_IMAGE_WHITE from '/icons/turbine_outline-white.svg';
import MARKER_BLUE from '/icons/marker-blue.png';
import CIRCLE_MINOR_ICON from '/icons/circle-minor.svg';
import CIRCLE_GOOD_ICON from '/icons/circle-good.svg';
import SQUARE_POOR_ICON from '/icons/square-poor.svg';
import TRIANGLE_MAJOR_ICON from '/icons/triangle-major.svg';
import TRIANGLE_CRITICAL_ICON from '/icons/triangle-critical.svg';
import i18n from 'i18next';

export {
  CIRCLE_GOOD_ICON,
  CIRCLE_MINOR_ICON,
  FULL_TURBINE_IMAGE,
  FULL_TURBINE_IMAGE_WHITE,
  MARKER_BLUE,
  SKYSPECS_BRAND_ICON_WHITE,
  SKYSPECS_BRAND_LOGO,
  SKYSPECS_BRAND_LOGO_WHITE,
  SQUARE_POOR_ICON,
  TRIANGLE_CRITICAL_ICON,
  TRIANGLE_MAJOR_ICON,
  TURBINE_ICON,
};

// App constants
export const TABLE_SCROLL_WIDTH = 1280;
export const MIN_TABLE_SCROLL_HEIGHT = 600;
export const TABLE_SCROLL_WIDTH_SMALL = 640;
export const COMPONENT_PICKER_WIDTH_SMALL = 1140;
export const STANDARD_COLUMN_SCROLL_Y = { y: 250 };

// format constants
export const DATE_FORMAT = 'YYYY-MM-DD';
export const TIME_FORMAT = 'HH:mm';
export const TIME_WITH_SECONDS_FORMAT = 'HH:mm:ss';
export const MONTH_YEAR_FORMAT = 'YYYY-MM';

// Placeholders for a value we have nothing to render for. Both characters are in
// use: the em dash reads as an intentional blank in a table cell, the hyphen
// suits inline text and tighter labels.
export const EM_DASH = '—';
export const HYPHEN = '-';

// Field type constants
export const NUMBER = 'NUMBER';
export const TIME_RANGE = 'TIME_RANGE';
export const DATE = 'DATE';

// Work container field type constants
export const SELECT = 'SELECT';
export const MULTI_SELECT = 'MULTI_SELECT';
export const TEXT = 'TEXT';

// Tasks
export const TASK_TYPE_REPAIR = 'REPAIR';
export const REPAIR_TASK = 'REPAIR_TASK';
export const TASK_TYPE_OTHER = 'OTHER';
export const OTHER_TASK = 'OTHER_TASK';
export const OTHER_TASK_TYPE_NAME = 'OtherAssetTask';
export const TASK_TYPE_INSPECT = 'INSPECT';
export const INSPECT_TASK = 'INSPECT_TASK';
export const TASK_TYPE_INTERNAL_BLADE_INSPECTION = 'INTERNAL_BLADE_INSPECTION';
export const INTERNAL_BLADE_INSPECTION_TASK = 'INTERNAL_BLADE_INSPECTION_TASK';
export const TASK_TYPE_INSPECTION = 'INSPECTION';
export const INSPECTION_TASK = 'INSPECTION_TASK';
export const MAINTENANCE_ACTIVITY = 'maintenance-activity';
export const TASK_LEVEL_TYPES = [
  'REPAIR_TASK',
  'INSPECT_TASK',
  'INTERNAL_BLADE_INSPECTION_TASK',
  'OTHER_TASK',
  'INSPECTION_TASK',
];
export const INSPECTION_TASK_DISPLAY_NAME = i18n.t('task.inspectionTask', 'Inspection Task');
export const OTHER_TASK_DISPLAY_NAME = i18n.t('task.otherTask', 'Other Task');
export const INTERNAL_BLADE_INSPECTION_DISPLAY_NAME = i18n.t(
  'task.type.internalBladeInspection',
  'Internal Blade Inspection'
);
export const DEPRECATED_TASK_CREATION_TYPES = [INTERNAL_BLADE_INSPECTION_TASK];

// Work Orders / Campaigns
export const CAMPAIGN = 'CAMPAIGN';
export const WORK_CAMPAIGN = 'WORK_CAMPAIGN';
export const WORK_ORDER = 'WORK_ORDER';

// Assets
export const ASSET_TYPE_SITE = 'Site';
export const ASSET_TYPE_LOCATION = 'Location';
export const ASSET_TYPE_TURBINE = 'Turbine';
export const ASSET_TYPE_BLADE = 'Blade';
export const NACELLE_ASSET_TYPE = 'Nacelle';
export const ASSET_TYPE_GENERIC = 'GenericAsset';
export const BOP_ASSET_TYPES = ['transmission_structure', 'substation'] as const;

// inspection vendor
export const CORNIS = 'cornis';
export const SKYSPECS = 'skyspecs';
export const THIRD_PARTY = 'third party';

export const TASK_STATUSES = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETE: 'COMPLETE',
};

// Getters, so each read translates in the current language rather than the one at import time.
export const TASK_STATUS_LABELS = {
  get PENDING() {
    return i18n.t('workOrder.status.pending', 'Pending');
  },
  get IN_PROGRESS() {
    return i18n.t('workOrder.status.inProgress', 'In Progress');
  },
  get COMPLETE() {
    return i18n.t('workOrder.complete', 'Complete');
  },
};

export const WORK_CONTAINER_FIELD_TYPE_OPTIONS = {
  [TEXT]: i18n.t('settings.text', 'Text'),
  [NUMBER]: i18n.t('settings.number', 'Number'),
  [DATE]: i18n.t('settings.date', 'Date'),
  [SELECT]: i18n.t('settings.fieldTypeOptions.dropdown', 'Dropdown'),
  // [MULTI_SELECT]: 'Multiple Selection',  FIXME: Uncomment this when we can allow multi-select
};

// FIXME: when we can allow multi-select we can remove this and just use WORK_CONTAINER_FIELD_TYPE_OPTIONS for the labels in Field.jsx
export const WORK_CONTAINER_TAG_LABELS = {
  ...WORK_CONTAINER_FIELD_TYPE_OPTIONS,
  [MULTI_SELECT]: i18n.t('settings.fieldTypeOptions.multipleSelection', 'Multiple Selection'),
};

export const WORK_CONTAINER_TYPE_OPTIONS = {
  [CAMPAIGN]: i18n.t('table.campaign', 'Campaign'),
  [WORK_ORDER]: i18n.t('table.workOrder', 'Work Order'),
  [REPAIR_TASK]: i18n.t('settings.repairTask', 'Repair Task'),
  [INSPECT_TASK]: i18n.t('settings.inspectTask', 'Inspect Task'),
  [INTERNAL_BLADE_INSPECTION_TASK]: i18n.t(
    'task.type.internalBladeInspectionTask',
    'Internal Blade Inspection Task'
  ),
  [OTHER_TASK]: i18n.t('settings.otherTask', 'Other Task'),
};

export const WORK_CONTAINER = 'WORK_CONTAINER';

export const SEVERITIES = [1, 2, 3, 4, 5];

export const INGESTION_CODES = {
  NO_INSPECTION: 'No inspection in progress for turbine',
};

export const TABLE_FILTER_OPERATORS = {
  EQUALS: 'EQUALS',
  NOT_EQUALS: 'NOT_EQUALS',
  CONTAINS: 'CONTAINS',
  GREATER_THAN: 'GREATER_THAN',
  GREATER_THAN_OR_EQUAL: 'GREATER_THAN_OR_EQUAL',
  LESS_THAN: 'LESS_THAN',
  LESS_THAN_OR_EQUAL: 'LESS_THAN_OR_EQUAL',
  IS_NULL: 'IS_NULL',
  IS_NOT_NULL: 'IS_NOT_NULL',
  ARRAY_INCLUDES: 'ARRAY_INCLUDES',
};

/**
 * A filter value that is really an operator sentinel. `paging.js` reads the operator from
 * the first filter value, so a sentinel occupies the whole `filterBy` entry and cannot sit
 * alongside real values.
 */
export const isTableFilterOperator = (value: unknown): boolean =>
  Object.values(TABLE_FILTER_OPERATORS).includes(String(value));

// regex for checking for a valid UUID (ex. 82d8f533-03c9-439e-8502-e26093a07c87)
export const uuidRegex =
  '([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})';

// regex for checking for a valid ID (ex. 82D8F533 or 82D8F533-5 )
export const idRegex = '[0-9a-z]{8}(-[0-9]+)?';

// Regex to insert commas between groups of 3 digits in numbers
export const numberCommaRegex = /\B(?=(\d{3})+(?!\d))/g;

// TODO: Update values and reference for inspection
// filters once INSPECTIONS_MERGE feature flag
// has been removed
export const INSPECTION_UPLOAD_STATUSES = {
  DRAFT: 'DRAFT',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
};
export const BLADE_STATUSES = {
  ACTIVE: 'ACTIVE',
  INACTIVE_STORAGE: 'INACTIVE_STORAGE',
  INACTIVE_EOL: 'INACTIVE_EOL',
};

export const BLADE_STATUS_LABELS = {
  ACTIVE: i18n.t('settings.active', 'Active'),
  INACTIVE_STORAGE: i18n.t('assets.storage', 'Storage'),
  INACTIVE_EOL: i18n.t('blade.status.recycled', 'Recycled'),
};

// wind inspection type names
export const AUTONOMOUS_DRONE = 'Autonomous Drone';
export const BLADE_EXTERNAL = 'Blade External';
export const BLADE_EXTERNAL_OTHER = 'Blade External Other';
export const GROUND_PHOTO = 'Ground Photo';
export const MANUAL_DRONE = 'Manual Drone';
export const ROPE_INSPECTION = 'Rope Inspection';
export const BLADE_INTERNAL = 'Blade Internal';
export const BLADE_INTERNAL_LEGACY = 'Blade Internal (Legacy)';
export const TOWER_EXTERNAL = 'Tower External';
export const TRANSITION_PIECE = 'Transition Piece';
export const NACELLE_EXTERNAL = 'Nacelle External';
export const GEARBOX_BORESCOPE = 'Gearbox Borescope';

// site inspection type names
export const AERIAL_OVERVIEW = 'Aerial Overview';
export const BALANCE_OF_PLANT = 'Balance of Plant';

export const isCrossSiteInspection = (inspectionType?: string) =>
  inspectionType === AERIAL_OVERVIEW;

// solar inspection type names
export const SOLAR_AERIAL_PHOTO = 'Solar Aerial Photo';
export const SOLAR_AERIAL_THERMAL = 'Solar Aerial Thermal';
export const SOLAR_BACK_OF_PANEL = 'Solar Back of Panel';
export const SOLAR_INVERTER = 'Solar Inverter';
export const SOLAR_COMBINER = 'Solar Combiner';

// generic inspection type names
export const GENERIC = 'Generic';

/*** wind inspection helpers ********************/
export const WIND_INSPECTION_TYPES = [
  AUTONOMOUS_DRONE,
  BLADE_EXTERNAL,
  BLADE_EXTERNAL_OTHER,
  GROUND_PHOTO,
  MANUAL_DRONE,
  ROPE_INSPECTION,
  BLADE_INTERNAL,
  BLADE_INTERNAL_LEGACY,
  TOWER_EXTERNAL,
  TRANSITION_PIECE,
  NACELLE_EXTERNAL,
  GEARBOX_BORESCOPE,
];

export const isWindInspection = (inspectionType?: string) =>
  !!inspectionType && WIND_INSPECTION_TYPES.includes(inspectionType);

export const EXTERNAL_BLADE_INSPECTION_TYPES = [
  AUTONOMOUS_DRONE,
  BLADE_EXTERNAL,
  BLADE_EXTERNAL_OTHER,
  GROUND_PHOTO,
  MANUAL_DRONE,
  ROPE_INSPECTION,
];
export const INTERNAL_BLADE_INSPECTION_TYPES = [BLADE_INTERNAL];

export const isBladeInspection = (inspectionType?: string) =>
  !!inspectionType &&
  [...EXTERNAL_BLADE_INSPECTION_TYPES, ...INTERNAL_BLADE_INSPECTION_TYPES].includes(inspectionType);

export const isExternalInspection = (inspectionType?: string) =>
  !!inspectionType && EXTERNAL_BLADE_INSPECTION_TYPES.includes(inspectionType);

export const isInternalInspection = (inspectionType?: string) =>
  !!inspectionType && INTERNAL_BLADE_INSPECTION_TYPES.includes(inspectionType);

export const isTowerOrTPInspection = (inspectionType?: string) =>
  !!inspectionType && [TOWER_EXTERNAL, TRANSITION_PIECE].includes(inspectionType);

export const isTowerInspection = (inspectionType?: string) =>
  !!inspectionType && inspectionType === TOWER_EXTERNAL;

export const isTPInspection = (inspectionType?: string) =>
  !!inspectionType && inspectionType === TRANSITION_PIECE;

export const isNacelleInspection = (inspectionType?: string) =>
  !!inspectionType && inspectionType === NACELLE_EXTERNAL;

export const isGearboxInspection = (inspectionType?: string) =>
  !!inspectionType && inspectionType === GEARBOX_BORESCOPE;

/*** solar inspection helpers ********************/
export const SOLAR_INSPECTION_TYPES = [
  SOLAR_AERIAL_PHOTO,
  SOLAR_AERIAL_THERMAL,
  SOLAR_BACK_OF_PANEL,
  SOLAR_INVERTER,
  SOLAR_COMBINER,
];

export const DRONE_POSITION_MAP_INSPECTION_TYPES = [
  SOLAR_BACK_OF_PANEL,
  SOLAR_INVERTER,
  SOLAR_COMBINER,
  BALANCE_OF_PLANT,
];

export const isSolarInspection = (inspectionType?: string) =>
  !!inspectionType && SOLAR_INSPECTION_TYPES.includes(inspectionType);

export const MAP_INSPECTION_TYPES = [...SOLAR_INSPECTION_TYPES, AERIAL_OVERVIEW, BALANCE_OF_PLANT];

export const isMapInspection = (inspectionType?: string) =>
  !!inspectionType && MAP_INSPECTION_TYPES.includes(inspectionType);

export const NORTH_ROTATION_DISABLED_INSPECTION_TYPES = [
  BALANCE_OF_PLANT,
  SOLAR_BACK_OF_PANEL,
  SOLAR_COMBINER,
  SOLAR_INVERTER,
];

/*** generic inspection helpers ********************/
export const isGenericInspection = (inspectionType?: string) =>
  !!inspectionType && inspectionType === GENERIC;

export enum TargetDistanceType {
  BLADE = 'BLADE',
  TOWER_TP_HEIGHT = 'TOWER_TP_HEIGHT',
  TOWER_TP_DISTANCE = 'TOWER_TP_DISTANCE',
  OTHER = 'OTHER',
}

// For most Inspection displays we only need to know if the type is "Nacelle", "Tower or TP", or "Blade". This compresses the type to four options so we can use more simple logic.
export const getTargetDistanceTypeWithoutPictures = (
  inspectionType: string | undefined,
  nacelleSides?: string[],
  towerSides?: string[]
): TargetDistanceType => {
  if (isBladeInspection(inspectionType)) {
    return TargetDistanceType.BLADE;
  }
  if (isTowerOrTPInspection(inspectionType)) {
    if (nacelleSides) {
      return TargetDistanceType.TOWER_TP_DISTANCE;
    }
    if (towerSides) {
      return TargetDistanceType.TOWER_TP_HEIGHT;
    }
  }
  return TargetDistanceType.OTHER;
};

export type DropdownOptionsType = {
  id: number;
  label: string;
  order: number;
  value: string;
};

export type ReadableOptionsType = {
  [key: string]: {
    label: string;
    order: number;
  };
};

export const READABLE_SIDE_OPTIONS: ReadableOptionsType = {
  leading_edge: {
    label: i18n.t('table.leadingEdge', 'Leading Edge'),
    order: 1,
  },
  pressure_side: {
    label: i18n.t('table.pressureSide', 'Pressure Side'),
    order: 2,
  },
  suction_side: {
    label: i18n.t('table.suctionSide', 'Suction Side'),
    order: 3,
  },
  trailing_edge: {
    label: i18n.t('table.trailingEdge', 'Trailing Edge'),
    order: 4,
  },
  ungrouped: {
    label: i18n.t('inspection.sideOptions.ungrouped', 'ungrouped'),
    order: 5,
  },
};

export const READABLE_CHAMBER_OPTIONS: ReadableOptionsType = {
  'trailing-edge': {
    label: i18n.t('table.trailingEdge', 'Trailing Edge'),
    order: 4,
  },
  trailing_edge: {
    label: i18n.t('table.trailingEdge', 'Trailing Edge'),
    order: 4,
  },
  root: {
    label: i18n.t('inspection.chamberOptions.root', 'Root'),
    order: 0,
  },
  'leading-edge': {
    label: i18n.t('table.leadingEdge', 'Leading Edge'),
    order: 1,
  },
  leading_edge: {
    label: i18n.t('table.leadingEdge', 'Leading Edge'),
    order: 1,
  },
  'between-web-1': {
    label: i18n.t('inspection.chamberOptions.betweenWeb1', 'Between Web 1'),
    order: 2,
  },
  between_web_1: {
    label: i18n.t('inspection.chamberOptions.betweenWeb1', 'Between Web 1'),
    order: 2,
  },
  'between-web-2': {
    label: i18n.t('inspection.chamberOptions.betweenWeb2', 'Between Web 2'),
    order: 3,
  },
  between_web_2: {
    label: i18n.t('inspection.chamberOptions.betweenWeb2', 'Between Web 2'),
    order: 3,
  },
  ungrouped: {
    label: i18n.t('inspection.sideOptions.ungrouped', 'ungrouped'),
    order: 5,
  },
};

export const READABLE_TOWER_SIDE_OPTIONS: ReadableOptionsType = {
  north: {
    label: i18n.t('inspection.towerSide.north', 'north'),
    order: 1,
  },
  east: {
    label: i18n.t('inspection.towerSide.east', 'east'),
    order: 2,
  },
  south: {
    label: i18n.t('inspection.towerSide.south', 'south'),
    order: 3,
  },
  west: {
    label: i18n.t('inspection.towerSide.west', 'west'),
    order: 4,
  },
  ungrouped: {
    label: i18n.t('inspection.sideOptions.ungrouped', 'ungrouped'),
    order: 5,
  },
};

export const READABLE_NACELLE_SIDE_OPTIONS: ReadableOptionsType = {
  front: {
    label: i18n.t('inspection.nacelleSide.front', 'front'),
    order: 1,
  },
  left: {
    label: i18n.t('inspection.nacelleSide.left', 'left'),
    order: 2,
  },
  back: {
    label: i18n.t('inspection.nacelleSide.back', 'back'),
    order: 3,
  },
  right: {
    label: i18n.t('inspection.nacelleSide.right', 'right'),
    order: 4,
  },
  top: {
    label: i18n.t('inspection.nacelleSide.top', 'top'),
    order: 5,
  },
  bottom: {
    label: i18n.t('inspection.nacelleSide.bottom', 'bottom'),
    order: 6,
  },
  ungrouped: {
    label: i18n.t('inspection.sideOptions.ungrouped', 'ungrouped'),
    order: 7,
  },
};

export const IMAGE_AND_METADATA_UPLOAD_OPTIONS = {
  SKYSPECS_JSON: 'inspection-json',
  SKYSPECS_CSV: 'inspection-csv',
  CORNIS: 'inspection-cornis',
  AERO_ENTERPRISE: 'inspection-aero-enterprise',
  ROBOTICS_UPLOAD: 'robotics',
};

export const METADATA_UPLOAD_OPTIONS = {
  SKYSPECS_JSON: 'inspection-metadata-json',
  SKYSPECS_CSV: 'inspection-metadata-csv',
  CORNIS: 'inspection-metadata-cornis',
  UNUSED: 'inspection-metadata-ndjson',
  CORNIS_BULK: 'inspection-bulk-cornis',
  CORNIS_BULK_VALIDATE: 'inspection-bulk-cornis-validate',
  CORNIS_BULK_VALIDATED: 'inspection-bulk-cornis-validated',
};

export const CORNIS_BULK_UPLOAD_OPTIONS = {
  CORNIS_BULK: 'inspection-bulk-cornis',
  CORNIS_BULK_VALIDATE: 'inspection-bulk-cornis-validate',
  CORNIS_BULK_VALIDATED: 'inspection-bulk-cornis-validated',
  CORNIS_BULK_GENERATE: 'inspection-bulk-cornis-generate',
};

export const CSV_BULK_UPLOAD_OPTIONS = {
  CSV_BULK: 'inspection-bulk-csv',
  CSV_BULK_VALIDATE: 'inspection-bulk-csv-validate',
  CSV_BULK_VALIDATED: 'inspection-bulk-csv-validated',
  CSV_BULK_GENERATE: 'inspection-bulk-csv-generate',
};

export const JSON_BULK_UPLOAD_OPTIONS = {
  JSON_BULK: 'inspection-bulk-json',
  JSON_BULK_VALIDATE: 'inspection-bulk-json-validate',
  JSON_BULK_VALIDATED: 'inspection-bulk-json-validated',
  JSON_BULK_GENERATE: 'inspection-bulk-json-generate',
};

export const SKYSPECS_ORG = '28435de4-15bb-11e9-a171-f3fcecd20738';
export const VESTAS_EUROPE_ORG = '739c2327-353e-4a60-87f3-66c3b9435ca2'; // Vestas Wind Systems - EU

// Organizations that can send Inspections to the Vestas CIR integration.
// Add SkySpecs org ID here to enable it for testing.
export const CIR_ALLOWED_ORG_IDS: readonly string[] = [VESTAS_EUROPE_ORG];

export const PICTURE_NOT_FOUND_ERR_MESSAGE = 'Picture not found.';
export const UNABLE_TO_LOAD_IMG_ERR_MESASAGE = i18n.t(
  'imageViewer.unableToLoadImage',
  'Unable to load image. Please go back or refresh the page.'
);

// This is the classname for the actual element that emits scroll events
export const APP_SCROLL_CONTAINER_CLASSNAME = '.scrollable-app-content-wrapper';
